import axios, { type AxiosInstance } from "axios";

// VITE_API_BASE === "" (dev .env.local) debe respetarse como path-relative para
// que las llamadas pasen por el proxy de Vite. Usamos chequeo de undefined en vez
// de `||` porque "" es falsy y caería al default de prod, bypasseando el proxy.
const RAW_API_BASE = import.meta.env.VITE_API_BASE;
export const API_BASE: string =
  RAW_API_BASE !== undefined ? RAW_API_BASE : "https://auth03.realvirtual.com.mx";

let inMemoryToken: string | null = null;
export function setBearerToken(token: string | null) {
  inMemoryToken = token;
}
export function getBearerToken(): string | null {
  return inMemoryToken;
}

export const api: AxiosInstance = axios.create({
  baseURL: API_BASE,
  timeout: 15000,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((cfg) => {
  if (inMemoryToken) {
    cfg.headers = cfg.headers || {};
    (cfg.headers as Record<string, string>).Authorization = `Bearer ${inMemoryToken}`;
  }
  return cfg;
});

export interface CartItem {
  id?: string;
  sku: string;
  idAplicacion: number;
  meterType: string;
  quantity: number;
  unitPrice: string;
  lineTotal: string;
  title?: string;
  meta?: string;
}

export interface CartSnapshot {
  items: CartItem[];
  subtotal: string;
  tax: string;
  total: string;
  currency: string;
  deferredRedemptionCode?: string;
  sourceQuoteToken?: string;
  quoteId?: number;
}

export interface StartResponse {
  sessionId: string;
  token: string;
  cart: CartSnapshot;
  nextStep: "identity" | "redeem";
  expiresAt: string;
}

export interface SessionState {
  sessionId: string;
  status: string;
  cart: CartSnapshot;
  expiresAt: string;
  paymentIntentId: string | null;
  idEmpresa: number | null;
}

export interface IdentityResponse {
  cognitoSub: string;
  alreadyProvided: boolean;
}

export interface RedeemResponse {
  provisioned: boolean;
  queued: boolean;
  /** ADR-CMRC-013: qué resolvió el backend para el credential enviado. */
  sourceType?: "redemption" | "cfdi" | "quote";
  sourceId?: string;
  paqueteId?: number | null;
  planId?: number | null;
  idEmpresaTemplate?: number | null;
}

export interface CheckoutResponse {
  intentId: string;
  initPoint: string;
}

// SE-159ONB-READY corrective 2: does NOT call setBearerToken itself. With the
// App entry point mounted in React.StrictMode, the dev double-invoke of the
// bootstrap effect can start two of these concurrently; only the caller
// knows (via its own bootstrap generation guard) whether this particular
// call is still the accepted one by the time it resolves. Setting the
// global in-memory bearer here unconditionally would let a superseded call
// clobber the accepted session's token regardless of that guard.
export async function startWizard(body: {
  redemptionCode?: string;
  quoteToken?: string;
  productKeys?: string[];
  qty?: number[];
}): Promise<StartResponse> {
  const { data } = await api.post<StartResponse>(
    "/api/v3/onboarding/wizard/start",
    body,
  );
  return data;
}

export async function getSession(sessionId: string): Promise<SessionState> {
  const { data } = await api.get<SessionState>(
    `/api/v3/onboarding/wizard/${sessionId}`,
  );
  return data;
}

export type WizardSessionStatus =
  | "started"
  | "identity_provided"
  | "payment_pending"
  | "payment_captured"
  | "provisioned"
  | "abandoned"
  | "failed";

export interface WizardTerminalInfo {
  errorCode: "WIZARD_SESSION_TERMINAL";
  status: WizardSessionStatus;
}

// wizardSessionAuth (synapta-secure) intercepts terminal-status sessions
// before the GET handler runs and returns 410 { error_code, status } instead
// of the normal 200 body. This is the only shape that authoritatively
// confirms provisioning finished (status:"provisioned") or won't
// (status:"failed"|"abandoned"). Any other error shape is unconfirmed, not
// a negative result — callers must not infer success/failure from it.
export function parseWizardTerminal(e: unknown): WizardTerminalInfo | null {
  if (typeof e !== "object" || e === null) return null;
  const anyE = e as {
    response?: { status?: number; data?: { error_code?: string; status?: string } };
  };
  const res = anyE.response;
  if (
    res?.status === 410 &&
    res.data?.error_code === "WIZARD_SESSION_TERMINAL" &&
    typeof res.data?.status === "string"
  ) {
    return {
      errorCode: "WIZARD_SESSION_TERMINAL",
      status: res.data.status as WizardSessionStatus,
    };
  }
  return null;
}

export async function postIdentity(
  sessionId: string,
  body: {
    email: string;
    password: string;
    nombre: string;
    rfc?: string;
    telefono?: string;
  },
): Promise<IdentityResponse> {
  const { data } = await api.post<IdentityResponse>(
    `/api/v3/onboarding/wizard/${sessionId}/identity`,
    body,
  );
  return data;
}

export async function postRedeem(
  sessionId: string,
  // ADR-CMRC-013: `credential` acepta code UUID | CFDI uuid | folio de
  // cotización. El backend mantiene `code` como alias legacy; el FE ya
  // manda siempre `credential`.
  body: { credential: string; email: string },
): Promise<RedeemResponse> {
  const { data } = await api.post<RedeemResponse>(
    `/api/v3/onboarding/wizard/${sessionId}/redeem`,
    body,
  );
  return data;
}

export async function postCheckout(
  sessionId: string,
  body: { subscription?: "monthly" | "yearly" },
): Promise<CheckoutResponse> {
  const { data } = await api.post<CheckoutResponse>(
    `/api/v3/onboarding/wizard/${sessionId}/checkout`,
    body,
  );
  return data;
}

// ─────────────────────────────────────────────────────────────────────────
// ARES-CORE-001 — PIN / first-user contract
// Backend: docs/ares/ARES_ONBOARDING_INVARIANTS_ARCH.md (synapta-secure)
// ─────────────────────────────────────────────────────────────────────────

export interface PinStatus {
  isSet: boolean;
  forceChange: boolean;
}

export interface MeResponse {
  uid: string;
  email?: string;
  pin?: PinStatus;
  [k: string]: unknown;
}

export interface PinVerifyResponse {
  ok: boolean;
  actionToken?: string;
  mustChangePin?: boolean;
}

export interface PinVerifyRequest {
  pin: string;
  permissionKey: string;
  empresaId: number;
  resourceId?: string | number;
  justification?: string;
}

export interface PinChangeBody {
  currentPin?: string;
  newPin: string;
}

export interface InitialUserResponse {
  user: {
    uid: string;
    idEmpresa: number;
    email: string;
    nombre: string;
  };
  initialPin: string;
}

export interface ProvisionFirstUserBody {
  nombre: string;
  correo: string;
  telefono?: string;
  aplicaciones?: Array<{ idAplicacion: number; idAplicacionMenu?: number }>;
}

export async function fetchMe(): Promise<MeResponse> {
  const { data } = await api.get<MeResponse>("/api/v3/me");
  return data;
}

export async function verifyPin(
  body: PinVerifyRequest,
): Promise<PinVerifyResponse> {
  const { data } = await api.post<PinVerifyResponse>(
    "/api/v3/auth/pin/verify",
    body,
    { headers: { "x-empresa-id": String(body.empresaId) } },
  );
  return data;
}

export async function changePin(
  body: PinChangeBody,
): Promise<{ ok: boolean }> {
  const { data } = await api.post<{ ok: boolean }>(
    "/api/v3/auth/pin/change",
    body,
  );
  return data;
}

export async function provisionFirstUser(
  idEmpresa: number,
  body: ProvisionFirstUserBody,
): Promise<InitialUserResponse> {
  const { data } = await api.post<InitialUserResponse>(
    `/api/v3/empresas/${idEmpresa}/users/first`,
    body,
  );
  return data;
}

// Response interceptor: backend signals force-change either via top-level
// mustChangePin (e.g. /pin/verify) or via /me's pin.forceChange. Emit a
// global event so App.tsx mounts the SetTemporaryPinModal regardless of
// which screen made the call.
api.interceptors.response.use(
  (res) => {
    try {
      const d = res?.data as Record<string, unknown> | undefined;
      const mustChange =
        d?.mustChangePin === true ||
        (typeof d?.pin === "object" &&
          d?.pin !== null &&
          (d.pin as PinStatus).forceChange === true);
      if (mustChange) {
        window.dispatchEvent(new CustomEvent("synapta:must-change-pin"));
      }
    } catch {
      // never let interceptor side-effects break the response
    }
    return res;
  },
  (err) => Promise.reject(err),
);
